import React, { useState, useEffect, useContext } from "react";
import cx from "classnames";
import styles from "../scss/AFSubmit.module.scss";
import NiceWorkIMG from "../assets/images/illustration-nice-work.svg";
import shareIMG from "../assets/images/nice-work-share.svg";
import feedbackIMG from "../assets/images/nice-work-feedback.svg";
import Button from "../../common/components/Forms/Button/Button";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import AFShareDialog from "../components/Modals/AFShareDialog";
import { Form, Formik } from "formik";
import Input from "../../common/components/Forms/Input/Input";
import { getContactInfo, getReferTerms, sendFinalComments } from "../api/ApiAppForm";
import { IContactInfo } from "../interfaces/IAppFormData";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import MobileNavMulticompare from "../../compare/components/MobileNavMulticompare/MobileNavMulticompare";
import AFTermsAndConditionsDialog from "../components/Modals/AFTermsAndConditionsDialog";
import { useParams } from "react-router-dom";
import { useAFForm } from "../hooks/useAFForm";
import { getProductTypeAsString } from "../../common/shared/enumHelpers";
import { PageType } from "../enums/PageType";

interface IAFDoneProps {
    applicationId: string;
}

const AFDonePage: React.FC = () => {
    const { selectors } = useContext(AppFormControllerContext);

    const params = useParams<IAFDoneProps>();
    const applicationId = params.applicationId;

    const [isOpenDialog, setIsOpenDialog] = useState(false);
    const [insurer, setInsurer] = useState("");
    const [feedback, setFeedback] = useState("");
    const [isShowFeedbackForm, setIsShowFeedbackForm] = useState(false);
    const [isFeedbackDone, setIsFeedbackDone] = useState(false);
    const [isShareDone, setIsSharekDone] = useState(false);
    const [isFeedbackSubmitting, setIsFeedbackSubmitting] = useState(false);
    const [contacts, setContacts] = useState<IContactInfo>();
    const [termsContent, setTermsContent] = useState("");
    const [isOpenTerms, setIsOpenTerms] = useState(false);

    const { checkValidApplication } = useAFForm({ pageType: PageType.Done, currentClientNumber: 0 });

    const handleFeedback = (value): void => {
        if (value.length <= 500) {
            setFeedback(value);
        }
    };

    const handleGetContactInfo = (): void => {
        const quoteGroupId = selectors.getQuoteGroupId();
        getContactInfo(quoteGroupId).then((res) => {
            setContacts(res);
        });
    };

    const handleComments = (): void => {
        if (!feedback) return;
        setIsFeedbackSubmitting(true);
        sendFinalComments(selectors.getApplicationId(), selectors.getQuoteGroupId(), feedback)
            .catch(() => useErrorLogger())
            .finally(() => {
                setIsFeedbackDone(true);
                setIsFeedbackSubmitting(true);
            });
    };

    const handleGetTerms = (): void => {
        getReferTerms().then((res) => {
            setTermsContent(res || "");
        });
    };

    const handleOpenTerms = (): void => {
        setIsOpenTerms(true);
    };

    useEffect(() => {
        if (!applicationId) {
            return;
        }
        checkValidApplication(applicationId, selectors);
        setInsurer(selectors.getInsurerName());
        handleGetContactInfo();
        handleGetTerms();
    }, [applicationId]);

    useEffect(() => {
        abTesting.convertAll(21, "", getProductTypeAsString, 0);
    }, []);

    return (
        <React.Fragment>
            <MobileNavMulticompare />
            <AFShareDialog
                isOpen={isOpenDialog}
                contacts={contacts}
                onClose={(isContinue: boolean) => {
                    if (isContinue) {
                        setIsSharekDone(true);
                    }
                    setIsOpenDialog(false);
                }}
                openTerms={handleOpenTerms}
            />
            <AFTermsAndConditionsDialog
                isOpen={isOpenTerms}
                onClose={() => {
                    setIsOpenTerms(false);
                }}
                content={termsContent}
                isReferFriend={true}
            />
            <div className={cx(styles["af-ownership"], styles["layout"])}>
                <div className={styles["af-done-page"]}>
                    <img src={NiceWorkIMG} alt="nice work icon" />
                    <div className={cx(styles["af-done-title-label"])}>Nice work!</div>

                    <p className={styles["af-done-title-desc"]}>
                        Congratulations on getting your insurance application under way!
                    </p>

                    <div className={styles["af-next-steps"]}>
                        <h2 className={styles["af-next-steps--title"]}>
                            What happens next?
                        </h2>

                        <h2 className={styles["af-next-steps--label"]}>1. Sent</h2>
                        <p className={styles["af-next-steps--info"]}>
                            Your application is sent to {insurer} insurance.
                        </p>

                        <h2 className={styles["af-next-steps--label"]}>2. Checked</h2>
                        <p className={styles["af-next-steps--info"]}>
                            If no more information is required, then your cover is issued!
                        </p>

                        <h2 className={styles["af-next-steps--label"]}>3. Follow ups</h2>
                        <p className={styles["af-next-steps--info"]}>
                            If more information is required, we'll contact you. More
                            information may need to be requested from your GP. Or/and{" "}
                            {insurer} may request a blood test/medical check. If the
                            outcome from the follow ups is accepted by {insurer} then your
                            cover will be issued.
                        </p>

                        <h2 className={styles["af-next-steps--label"]}>
                            4. "Special terms"
                        </h2>
                        <p className={styles["af-next-steps--info"]}>
                            In some cases, follow ups may result in "special terms" being
                            applied to your cover. We will discuss this with you first,
                            and if you are happy to proceed, we will get your cover
                            issued.
                        </p>
                    </div>

                    <div className={styles["af-share-wrapper"]}>
                        <img src={shareIMG} alt="share icon" />
                        <div className={styles["af-share-content"]}>
                            <h2 className={styles["af-share--title"]}>
                                Share LifeDirect with a friend
                            </h2>
                            <p className={styles["af-share--info"]}>
                                When they start a policy we'll send you both a $50
                                cashback!
                            </p>
                            {isShareDone ? (
                                <div className={styles["af-share--done"]}>
                                    <div className={styles["checkicon"]}></div>
                                    <p>Thank you. Your invite has been sent.</p>
                                </div>
                            ) : (
                                <Button
                                    className={cx("btn-secondary", "gap-m", styles["af-share--action"])}
                                    width="fill"
                                    component="button"
                                    size="m"
                                    type="button"
                                    onClick={() => setIsOpenDialog(true)}>
                                    <span>Share</span>
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={styles["af-share-wrapper"]}>
                        <img src={feedbackIMG} alt="feedback icon" />
                        <div className={styles["af-share-content"]}>
                            <h2 className={styles["af-share--title"]}>Help us improve</h2>
                            {isFeedbackDone ? (
                                <div className={styles["af-share--done"]}>
                                    <div className={styles["checkicon"]}></div>
                                    <p>Thank you. Your message has been sent.</p>
                                </div>
                            ) : (
                                <React.Fragment>
                                    <label
                                        className={styles["af-share--info"]}
                                        htmlFor="Do you have a comment or suggestion? We'd love
                                        your feedback!">
                                        Do you have a comment or suggestion? We'd love
                                        your feedback!
                                    </label>
                                    {isShowFeedbackForm ? (
                                        <Formik
                                            initialValues={{}}
                                            onSubmit={handleComments}>
                                            {() => (
                                                <Form autoComplete="off"
                                                    className={
                                                        styles["af-feedback-form"]
                                                    }>
                                                    <Input
                                                        className={cx("gap-m")}
                                                        id="Do you have a comment or suggestion? We'd love
                                                        your feedback!"
                                                        name="Do you have a comment or suggestion? We'd love
                                                        your feedback!"
                                                        type="textarea"
                                                        rows={3}
                                                        value={feedback}
                                                        onChange={(e) =>
                                                            handleFeedback(e.target.value)
                                                        }
                                                        placeholder="Your message"
                                                    />

                                                    <span
                                                        className={
                                                            styles["af-feedback__note"]
                                                        }>
                                                        {500 - feedback.length} characters
                                                        remaining
                                                    </span>

                                                    <Button
                                                        className={
                                                            cx("btn-secondary",
                                                                styles["af-feedback__send"])
                                                        }
                                                        width="fill"
                                                        component="button"
                                                        size="m"
                                                        type="submit"
                                                        isSubmitting={
                                                            isFeedbackSubmitting
                                                        }
                                                        isDisabled={!feedback}>
                                                        <span>Submit feedback</span>
                                                    </Button>
                                                    <Button
                                                        className={cx(
                                                            "gap-m",
                                                            styles["af-feedback__cancel"]
                                                        )}
                                                        width="fill"
                                                        component="button"
                                                        size="s"
                                                        onClick={() => {
                                                            setIsShowFeedbackForm(false);
                                                        }}
                                                        variant="default">
                                                        <span>Cancel</span>
                                                    </Button>
                                                </Form>
                                            )}
                                        </Formik>
                                    ) : (
                                        <Button
                                            className={cx(
                                                "btn-secondary",
                                                "gap-m",
                                                styles["af-share--action"]
                                            )}
                                            width="fill"
                                            component="button"
                                            size="m"
                                            type="button"
                                            onClick={() => {
                                                setIsShowFeedbackForm(true);
                                            }}>
                                            <span>Give feedback</span>
                                        </Button>
                                    )}
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default AFDonePage;
